<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import AppImage from '@/components/shared/AppImage.vue';
import Footer from '@/layout/Footer.vue';
import Nav from '@/layout/Nav.vue';
import { restaurantSchema } from '@/scripts/structured-data';

/**
 * Page-level shell: top banner, left nav, main content, optional sidebar image, footer.
 */
const props = defineProps<{
  /**
   * Filename stem of the per-page sidebar image (shown at `lg`+), or omitted for none.
   */
  sidebar?: string;
  /**
   * Extra classes applied to the `<main>` element.
   */
  mainClass?: string;
}>();

useHead({
  script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(restaurantSchema()) }],
});

/**
 * Derive a readable alt for the sidebar image from its stem (e.g. `images-our-story` → "Our
 * Story").
 */
const sidebarAlt = computed(() =>
  (props.sidebar ?? '')
    .replace(/^images-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()),
);
</script>

<template>
  <a
    href="#main"
    class="sr-only focus:not-sr-only focus:absolute focus:z-10 focus:bg-white focus:p-10"
  >
    Skip to content
  </a>
  <div class="relative mx-auto flex max-w-960 flex-auto flex-col bg-white">
    <RouterLink to="/">
      <AppImage
        name="albanos-villa"
        alt="Albano's Villa"
        sizes="(min-width: 960px) 960px, 100vw"
        img-class="w-full"
        eager
        preload
      />
    </RouterLink>
    <div class="flex-auto items-start gap-30 p-30 lg:flex">
      <Nav />
      <main id="main" :class="mainClass">
        <slot />
      </main>
      <div v-if="sidebar" class="w-220 shrink-0 max-lg:hidden">
        <AppImage :name="sidebar" :alt="sidebarAlt" sizes="220px" />
      </div>
    </div>
    <Footer />
  </div>
</template>
